import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { billingApi } from '../services/billingApi';
import type { PracticeSummaryDto } from '../types/billing';

interface PracticeContextValue {
  practices: PracticeSummaryDto[];
  currentPracticeId: string | null;
  setCurrentPracticeId: (practiceId: string) => void;
  isLoading: boolean;
  error: Error | null;
}

export const PracticeContext = createContext<PracticeContextValue | undefined>(undefined);

export const usePractice = () => {
  const ctx = useContext(PracticeContext);
  if (!ctx) throw new Error('usePractice must be used within PracticeProvider');
  return ctx;
};

// Persisted practice choice so a multi-practice operator isn't bounced back
// to the alphabetically-first practice on every reload.
const STORAGE_KEY = 'revmac.billing.currentPracticeId';

const readStoredPracticeId = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, blocked) - fall back to first practice.
    return null;
  }
};

export const PracticeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [practices, setPractices] = useState<PracticeSummaryDto[]>([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState<string | null>(readStoredPracticeId);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    billingApi
      .myPractices()
      .then((p) => {
        if (!cancelled) setPractices(p);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The selection only wins while it names one of the caller's practices; a
  // stale or revoked id falls back to the first practice (the pre-selector
  // behavior). The stored value is left in place so the choice survives a
  // temporarily-shortened practice list.
  const currentPracticeId =
    selectedPracticeId && practices.some((p) => p.id === selectedPracticeId)
      ? selectedPracticeId
      : practices[0]?.id ?? null;

  const setCurrentPracticeId = useCallback((practiceId: string) => {
    setSelectedPracticeId(practiceId);
    try {
      window.localStorage.setItem(STORAGE_KEY, practiceId);
    } catch {
      // Storage unavailable - the selection still applies for this session.
    }
  }, []);

  return (
    <PracticeContext.Provider
      value={{ practices, currentPracticeId, setCurrentPracticeId, isLoading, error }}
    >
      {children}
    </PracticeContext.Provider>
  );
};
